import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit, MessageSquare, History, Lock, Sparkles, Flag, ExternalLink, Mail, Phone, MapPin, Linkedin, Twitter, Globe, Share2, Trash2, Shield, Tag, Instagram, Facebook } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Layout } from '@/components/wiki/Layout';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { useSEO, generateArticleSchema, generateOrganizationSchema } from '@/hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WikiPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader, isAdmin, isSuperAdmin, refreshUser } = useAuth();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // Dynamic SEO for page
  useSEO({
    title: page?.title,
    description: page?.summary || (page ? `Learn about ${page.title} on Handshake, the business encyclopedia wiki. View company information, history, and business details.` : null),
    type: 'article',
    article: page ? {
      publishedTime: page.created_at,
      modifiedTime: page.updated_at,
      section: page.categories?.[0] || 'Business'
    } : null
  });

  // Add structured data to page
  useEffect(() => {
    if (!page) return;
    
    // Remove any existing structured data
    const existingScript = document.querySelector('script[data-page-schema]');
    if (existingScript) existingScript.remove();
    
    // Add article schema
    const articleSchema = generateArticleSchema(page);
    const orgSchema = generateOrganizationSchema(page);
    
    if (articleSchema || orgSchema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-schema', 'true');
      script.textContent = JSON.stringify([articleSchema, orgSchema].filter(Boolean));
      document.head.appendChild(script);
    }
    
    return () => {
      const script = document.querySelector('script[data-page-schema]');
      if (script) script.remove();
    };
  }, [page]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/pages/${slug}`);
        setPage(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Page not found');
        } else {
          setError('Failed to load page');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDelete = async () => {
    if (!user) {
      toast.error('Please log in to delete pages');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/pages/${slug}`, {
        headers: getAuthHeader()
      });
      toast.success('Page deleted successfully');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete page');
    } finally {
      setDeleting(false);
    }
  };

  const handleAdminClaim = async () => {
    if (!user || !isSuperAdmin()) return;

    try {
      await axios.post(`${API}/admin/claim-page/${page.id}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Page claimed successfully!');
      // Refresh page data
      const response = await axios.get(`${API}/pages/${slug}`);
      setPage(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim page');
    }
  };

  const handleAdminUnclaim = async () => {
    if (!user || !isSuperAdmin()) return;

    try {
      await axios.delete(`${API}/admin/unclaim-page/${page.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Sponsorship removed');
      // Refresh page data
      const response = await axios.get(`${API}/pages/${slug}`);
      setPage(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove sponsorship');
    }
  };

  const handleClaimWithCredit = async () => {
    if (!user) {
      toast.error('Please log in to claim this page');
      navigate('/login');
      return;
    }

    // Check if user has credits
    if (!user.claim_credits || user.claim_credits <= 0) {
      toast.info('You need claim credits to sponsor this page. Redirecting to pricing...');
      navigate('/pricing');
      return;
    }

    setClaiming(true);
    try {
      const response = await axios.post(`${API}/claim/${page.id}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message || 'Page claimed successfully!');
      // Refresh user data to update credit count
      await refreshUser();
      // Refresh page data to show sponsored status
      const pageResponse = await axios.get(`${API}/pages/${slug}`);
      setPage(pageResponse.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to claim page');
    } finally {
      setClaiming(false);
    }
  };

  // Determine if user can delete the page
  const canDelete = user && (
    page?.created_by === user.id || // Creator
    page?.sponsor_id === user.id || // Sponsor
    isAdmin() // Admin or super-admin
  );

  // For sponsored pages, only sponsor or admin can delete
  const canDeleteSponsored = user && (
    page?.sponsor_id === user.id ||
    isAdmin()
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-10 bg-[#eaecf0] rounded w-1/3 mb-4" />
            <div className="h-4 bg-[#eaecf0] rounded w-full mb-2" />
            <div className="h-4 bg-[#eaecf0] rounded w-full mb-2" />
            <div className="h-4 bg-[#eaecf0] rounded w-2/3" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !page) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-[#202122] mb-4">{error || 'Page not found'}</h1>
          <p className="text-[#54595d] mb-8">The page you're looking for doesn't exist or has been removed.</p>
          <Link to="/create">
            <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white" data-testid="create-new-page-btn">
              Create this Page
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const canEdit = user && (!page.is_locked || page.sponsor_id === user.id || ['admin', 'super-admin'].includes(user.role));

  return (
    <Layout>
      {/* Page Header */}
      <div className="bg-[#f8f9fa] border-b border-[#a2a9b1]" data-testid="wiki-page-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Tabs */}
            <Tabs defaultValue="article" className="w-auto">
              <TabsList className="bg-white border border-[#a2a9b1]">
                <TabsTrigger value="article" className="data-[state=active]:bg-[#eaecf0] data-[state=active]:text-[#202122] text-[#36c]" data-testid="article-tab">
                  Article
                </TabsTrigger>
                <TabsTrigger 
                  value="talk" 
                  onClick={() => navigate(`/wiki/${slug}/talk`)}
                  className="data-[state=active]:bg-[#eaecf0] data-[state=active]:text-[#202122] text-[#36c]"
                  data-testid="talk-tab"
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Talk
                </TabsTrigger>
                <TabsTrigger 
                  value="history" 
                  onClick={() => navigate(`/wiki/${slug}/history`)}
                  className="data-[state=active]:bg-[#eaecf0] data-[state=active]:text-[#202122] text-[#36c]"
                  data-testid="history-tab"
                >
                  <History className="h-4 w-4 mr-1" />
                  History
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleShare} className="text-[#36c] hover:text-[#202122] hover:bg-[#eaecf0]" data-testid="share-btn">
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </Button>
              <Link to={`/report?page=${page.id}`}>
                <Button variant="ghost" size="sm" className="text-[#36c] hover:text-[#202122] hover:bg-[#eaecf0]" data-testid="report-btn">
                  <Flag className="h-4 w-4 mr-1" />
                  Report
                </Button>
              </Link>
              {canEdit ? (
                <Link to={`/wiki/${slug}/edit`}>
                  <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white" size="sm" data-testid="edit-btn">
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </Link>
              ) : page.is_locked && (
                <Button variant="outline" size="sm" disabled className="border-[#a2a9b1] text-[#54595d]" data-testid="locked-btn">
                  <Lock className="h-4 w-4 mr-1" />
                  Locked
                </Button>
              )}
              {((page.is_sponsored || page.sponsor_id) ? canDeleteSponsored : canDelete) && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-[#d33] hover:text-[#b32424] hover:bg-[#fee7e6]" data-testid="delete-btn">
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white border-[#a2a9b1]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-[#202122]">Delete Page</AlertDialogTitle>
                      <AlertDialogDescription className="text-[#54595d]">
                        Are you sure you want to delete "{page.title}"? This action cannot be undone and will also delete all associated discussions and history.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-[#f8f9fa] border-[#a2a9b1] text-[#202122] hover:bg-[#eaecf0]">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        disabled={deleting}
                        className="bg-[#d33] hover:bg-[#b32424] text-white"
                      >
                        {deleting ? 'Deleting...' : 'Delete Page'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {/* Super Admin Claim/Unclaim */}
              {isSuperAdmin() && (
                page.is_sponsored ? (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleAdminUnclaim}
                    className="text-[#36c] hover:text-[#2a4b8d] hover:bg-[#eaecf0]" 
                    data-testid="unclaim-btn"
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    Unclaim
                  </Button>
                ) : (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleAdminClaim}
                    className="text-[#36c] hover:text-[#2a4b8d] hover:bg-[#eaecf0]" 
                    data-testid="admin-claim-btn"
                  >
                    <Shield className="h-4 w-4 mr-1" />
                    Claim (Admin)
                  </Button>
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="wiki-page-content">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Article */}
          <article className="lg:col-span-8 bg-white rounded-lg border border-[#a2a9b1] p-6 sm:p-8">
            {/* Title */}
            <div className="flex items-start justify-between mb-6 border-b border-[#a2a9b1] pb-4">
              <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#202122]" data-testid="page-title">
                {page.title}
              </h1>
              {page.is_sponsored && (
                <span className="flex items-center gap-1 px-2 py-1 bg-[#fee7e6] text-[#d33] text-xs font-medium rounded" data-testid="sponsored-badge">
                  <Sparkles className="h-3 w-3" />
                  Sponsored
                </span>
              )}
            </div>

            {/* Summary */}
            {page.summary && (
              <p className="text-lg text-[#54595d] mb-6 border-l-4 border-[#36c] pl-4 italic" data-testid="page-summary">
                {page.summary}
              </p>
            )}

            {/* Content */}
            <div 
              className="wiki-content" 
              dangerouslySetInnerHTML={{ __html: page.content }}
              data-testid="page-content"
            />

            {/* Citations/References */}
            {page.citations && page.citations.length > 0 && (
              <div className="references-section mt-8 pt-6 border-t border-[#c8ccd1]" data-testid="references-section">
                <h2 className="font-serif text-xl font-bold mb-4 text-[#202122]">References</h2>
                <ol className="list-decimal list-inside space-y-2 text-sm text-[#54595d]">
                  {page.citations.map((citation, index) => (
                    <li key={index} id={`ref-${index + 1}`} className="reference-item">
                      <span className="ref-number">[{index + 1}]</span>
                      {citation.url ? (
                        <a href={citation.url} target="_blank" rel="noopener noreferrer" className="text-[#36c] hover:underline">
                          {citation.text}
                        </a>
                      ) : (
                        citation.text
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Claim Page CTA */}
            {!page.is_sponsored && (
              <div className="mt-8 p-6 bg-[#f8f9fa] rounded-lg border border-[#a2a9b1]" data-testid="claim-cta">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-lg font-bold text-[#202122]">Claim this Page</h3>
                    <p className="text-sm text-[#54595d]">
                      {user?.claim_credits > 0 
                        ? `Use 1 of your ${user.claim_credits} credit${user.claim_credits > 1 ? 's' : ''} to become the sponsor.`
                        : 'Take control and add official contact information.'}
                    </p>
                  </div>
                  {user?.claim_credits > 0 ? (
                    <Button 
                      onClick={handleClaimWithCredit}
                      disabled={claiming}
                      className="bg-[#36c] hover:bg-[#2a4b8d] text-white whitespace-nowrap" 
                      data-testid="claim-page-btn"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      {claiming ? 'Claiming...' : 'Use Credit to Claim'}
                    </Button>
                  ) : (
                    <Link to="/pricing">
                      <Button className="bg-[#36c] hover:bg-[#2a4b8d] text-white whitespace-nowrap" data-testid="claim-page-btn">
                        <Sparkles className="h-4 w-4 mr-2" />
                        Claim this Page
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            )}
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Infobox */}
            {page.infobox && Object.keys(page.infobox).length > 0 && (
              <div className="infobox bg-[#f8f9fa] border border-[#a2a9b1] rounded-lg overflow-hidden" data-testid="infobox">
                <div className="infobox-header bg-[#eaecf0] text-[#202122] font-serif font-bold p-3 text-center border-b border-[#a2a9b1]">{page.title}</div>
                {page.infobox.image && (
                  <img 
                    src={page.infobox.image} 
                    alt={page.title} 
                    className="infobox-image w-full" 
                    width="300" 
                    height="200"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                {Object.entries(page.infobox).filter(([key]) => key !== 'image').map(([key, value]) => (
                  <div key={key} className="infobox-row flex border-b border-[#c8ccd1] last:border-b-0">
                    <span className="infobox-label bg-[#eaecf0] text-[#54595d] font-medium p-2 w-1/3 text-sm">{key.replace(/_/g, ' ')}</span>
                    <span className="infobox-value text-[#202122] p-2 w-2/3 text-sm">{value}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Categories */}
            {page.categories && page.categories.length > 0 && (
              <div className="bg-[#f8f9fa] rounded-lg border border-[#a2a9b1] p-4" data-testid="categories-section">
                <h3 className="font-medium text-[#202122] mb-3 flex items-center gap-2 text-sm">
                  <Tag className="h-4 w-4 text-[#36c]" />
                  Categories
                </h3>
                <div className="flex flex-wrap gap-2">
                  {page.categories.map((category, index) => (
                    <Link 
                      key={index}
                      to={`/search?category=${encodeURIComponent(category)}`}
                      className="px-3 py-1 bg-white border border-[#a2a9b1] rounded text-sm text-[#36c] hover:bg-[#eaecf0] transition-colors"
                    >
                      {category}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info (Premium) */}
            {page.is_sponsored && page.contact_info && (
              <div className="bg-[#f8f9fa] rounded-lg border border-[#a2a9b1] p-4" data-testid="contact-info">
                <h3 className="font-medium text-[#202122] mb-3 flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-[#36c]" />
                  Contact Information
                </h3>
                {/* Handle both array (new format) and object (old format) */}
                {Array.isArray(page.contact_info) ? (
                  <div className="space-y-4">
                    {page.contact_info.map((contact, index) => (
                      <div key={index} className={index > 0 ? 'pt-4 border-t border-[#c8ccd1]' : ''}>
                        {contact.name && (
                          <p className="text-[#202122] font-medium text-sm mb-2">{contact.name}</p>
                        )}
                        <div className="space-y-2 text-sm">
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-[#36c] hover:underline">
                              <Mail className="h-4 w-4" />
                              {contact.email}
                            </a>
                          )}
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-[#36c] hover:underline">
                              <Phone className="h-4 w-4" />
                              {contact.phone}
                            </a>
                          )}
                          {contact.address && (
                            <div className="flex items-start gap-2 text-[#54595d]">
                              <MapPin className="h-4 w-4 mt-0.5" />
                              {contact.address}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3 text-sm">
                    {page.contact_info.email && (
                      <a href={`mailto:${page.contact_info.email}`} className="flex items-center gap-2 text-[#36c] hover:underline">
                        <Mail className="h-4 w-4" />
                        {page.contact_info.email}
                      </a>
                    )}
                    {page.contact_info.phone && (
                      <a href={`tel:${page.contact_info.phone}`} className="flex items-center gap-2 text-[#36c] hover:underline">
                        <Phone className="h-4 w-4" />
                        {page.contact_info.phone}
                      </a>
                    )}
                    {page.contact_info.address && (
                      <div className="flex items-start gap-2 text-[#54595d]">
                        <MapPin className="h-4 w-4 mt-0.5" />
                        {page.contact_info.address}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Social Links (Premium) */}
            {page.is_sponsored && page.social_links && Object.keys(page.social_links).length > 0 && (
              <div className="bg-[#f8f9fa] rounded-lg border border-[#a2a9b1] p-4" data-testid="social-links">
                <h3 className="font-medium text-[#202122] mb-3 flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-[#36c]" />
                  Official Links
                </h3>
                <div className="space-y-2">
                  {page.social_links.website && (
                    <a href={page.social_links.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#36c] hover:underline text-sm">
                      <Globe className="h-4 w-4" />
                      Website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {page.social_links.linkedin && (
                    <a href={page.social_links.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#36c] hover:underline text-sm">
                      <Linkedin className="h-4 w-4" />
                      LinkedIn
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {page.social_links.twitter && (
                    <a href={page.social_links.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#36c] hover:underline text-sm">
                      <Twitter className="h-4 w-4" />
                      Twitter/X
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {page.social_links.instagram && (
                    <a href={page.social_links.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#36c] hover:underline text-sm">
                      <Instagram className="h-4 w-4" />
                      Instagram
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {page.social_links.facebook && (
                    <a href={page.social_links.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#36c] hover:underline text-sm">
                      <Facebook className="h-4 w-4" />
                      Facebook
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {/* Custom social links */}
                  {page.social_links.custom && page.social_links.custom.map((link, index) => (
                    <a key={index} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#36c] hover:underline text-sm">
                      <Globe className="h-4 w-4" />
                      {link.name}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </Layout>
  );
}
